import {
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import Container from "components/atoms/Container";
import Spinner from "components/atoms/Spinner";
import NavHeader from "components/organisms/NavHeader";

import { classNames } from "utils";

// The changelog is served through the products API (which proxies the
// env-appropriate CDN bucket server-side) to avoid browser CORS issues.
const API_BASE = process.env.REACT_APP_API_BASE as string;
const CHANGELOG_INDEX_URL = `${API_BASE}/products/changelog/index`;
const changelogEntryUrl = (id: string) =>
  `${API_BASE}/products/changelog/entries/${encodeURIComponent(id)}`;

const PAGE_SIZE = 12;

type Audience = "customer" | "internal";
type AudienceFilter = "all" | Audience;

interface ChangelogIndexItem {
  id: string;
  title: string;
  summary: string;
  date: string;
  generatedAt?: string;
  environment?: string;
  tags?: string[];
  highlights?: string[];
  url?: string;
}

interface ChangelogIndex {
  updatedAt?: string;
  entries: ChangelogIndexItem[];
}

interface ChangelogItem {
  title: string;
  description: string;
  audience: Audience;
  whereToFind?: string;
  docsUrl?: string;
}

interface ChangelogCategory {
  type: string;
  items: ChangelogItem[];
}

interface ChangelogEntry extends ChangelogIndexItem {
  version?: string | null;
  categories: ChangelogCategory[];
}

interface AvailableModel {
  tag: string;
  name: string;
  provider: string;
  supports_multimodal: boolean;
  zero_data_retention: boolean;
  reliable: boolean;
}

const CATEGORY_STYLES: Record<string, string> = {
  added: "bg-green-100 text-green-800",
  changed: "bg-blue-100 text-blue-800",
  improved: "bg-indigo-100 text-indigo-800",
  fixed: "bg-amber-100 text-amber-800",
  performance: "bg-purple-100 text-purple-800",
  deprecated: "bg-orange-100 text-orange-800",
  removed: "bg-red-100 text-red-800",
  security: "bg-rose-100 text-rose-800",
};

const formatDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
};

const monthKey = (iso: string): string => (iso || "").slice(0, 7);

const monthLabel = (key: string): string => {
  const d = new Date(`${key}-01T00:00:00Z`);
  if (isNaN(d.getTime())) return key;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  });
};

const fetchChangelogIndex = async (): Promise<ChangelogIndex> => {
  const res = await fetch(CHANGELOG_INDEX_URL, { cache: "no-store" });
  if (res.status === 404) return { entries: [] };
  if (!res.ok) throw new Error(`Failed to load changelog (${res.status})`);
  const data = (await res.json()) as ChangelogIndex;
  return { ...data, entries: data.entries || [] };
};

const fetchChangelogEntry = async (id: string): Promise<ChangelogEntry> => {
  const res = await fetch(changelogEntryUrl(id), { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to load entry (${res.status})`);
  return res.json();
};

const fetchAvailableModels = async (): Promise<AvailableModel[]> => {
  const res = await fetch(`${API_BASE}/products/models/available`);
  if (!res.ok) throw new Error(`Failed to load models (${res.status})`);
  return res.json();
};

const Tag = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
    {children}
  </span>
);

const ChangelogEntryCard = ({
  item,
  audience,
  defaultOpen,
}: {
  item: ChangelogIndexItem;
  audience: AudienceFilter;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(!!defaultOpen);

  const { data, isLoading, isError } = useQuery(
    ["changelog-entry", item.id],
    () => fetchChangelogEntry(item.id),
    { enabled: open, staleTime: 5 * 60 * 1000, retry: 1 }
  );

  const visibleCategories = useMemo(() => {
    if (!data?.categories) return [];
    if (audience === "all") return data.categories;
    return data.categories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter((it) => it.audience === audience),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [data, audience]);

  return (
    <article
      id={`entry-${item.id}`}
      className="scroll-mt-24 rounded-lg border border-gray-200 bg-white shadow-sm"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start justify-between gap-4 p-5 text-left"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-passio-indigo">
            <CalendarDaysIcon className="h-4 w-4" />
            {formatDate(item.date)}
          </div>
          <h3 className="mt-1 text-lg font-semibold text-gray-900">
            {item.title}
          </h3>
          <p className="mt-1 text-sm text-gray-600">{item.summary}</p>
          {(item.tags?.length || 0) > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {item.tags?.map((t) => (
                <Tag key={t}>{t}</Tag>
              ))}
            </div>
          )}
        </div>
        <span className="mt-1 flex-shrink-0 text-gray-400">
          {open ? (
            <ChevronUpIcon className="h-5 w-5" />
          ) : (
            <ChevronDownIcon className="h-5 w-5" />
          )}
        </span>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-5 pb-5 pt-4">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Spinner /> Loading details…
            </div>
          )}
          {isError && (
            <p className="text-sm text-red-600">
              Could not load the details for this entry.
            </p>
          )}
          {data && (
            <>
              {(data.highlights?.length || 0) > 0 && (
                <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-gray-700">
                  {data.highlights?.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              )}
              {visibleCategories.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No changes match the selected audience filter.
                </p>
              ) : (
                <div className="space-y-4">
                  {visibleCategories.map((cat) => (
                    <div key={cat.type}>
                      <span
                        className={classNames(
                          "inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold uppercase",
                          CATEGORY_STYLES[cat.type.toLowerCase()] ||
                            "bg-gray-100 text-gray-700"
                        )}
                      >
                        {cat.type}
                      </span>
                      <ul className="mt-2 space-y-3">
                        {cat.items.map((it, i) => (
                          <li key={i} className="text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">
                                {it.title}
                              </span>
                              {it.audience === "internal" && (
                                <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-600">
                                  internal
                                </span>
                              )}
                            </div>
                            <p className="whitespace-pre-line text-gray-600">
                              {it.description}
                            </p>
                            {it.whereToFind && (
                              <p className="mt-1 flex items-start gap-1.5 text-xs text-gray-500">
                                <MapPinIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                                <span>
                                  <span className="font-medium text-gray-600">
                                    Where:
                                  </span>{" "}
                                  {it.whereToFind}
                                </span>
                              </p>
                            )}
                            {it.docsUrl && (
                              <a
                                href={it.docsUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-passio-indigo hover:underline"
                              >
                                <BookOpenIcon className="h-3.5 w-3.5" />
                                Learn more
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </article>
  );
};

const ModelsSection = () => {
  const { data, isLoading, isError } = useQuery(
    ["available-models"],
    fetchAvailableModels,
    { staleTime: 10 * 60 * 1000, retry: 1 }
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <Spinner /> Loading models…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <p className="py-8 text-sm text-red-600">
        Could not load the list of available models.
      </p>
    );
  }

  const models = [...data]
    .filter((m) => m.reliable)
    .sort(
      (a, b) =>
        a.provider.localeCompare(b.provider) || a.name.localeCompare(b.name)
    );

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-3">Model</th>
            <th className="px-4 py-3">Provider</th>
            <th className="px-4 py-3">API tag</th>
            <th className="px-4 py-3 text-center">Multimodal</th>
            <th className="px-4 py-3 text-center">ZDR eligible</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {models.map((m) => (
            <tr key={m.tag}>
              <td className="px-4 py-3 font-medium text-gray-900">{m.name}</td>
              <td className="px-4 py-3 text-gray-600">{m.provider}</td>
              <td className="px-4 py-3">
                <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                  {m.tag}
                </code>
              </td>
              <td className="px-4 py-3 text-center">
                {m.supports_multimodal ? "Yes" : "—"}
              </td>
              <td className="px-4 py-3 text-center">
                {m.zero_data_retention ? "Yes" : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
        Showing generally-available models. ZDR-eligible models can be used with
        Zero Data Retention mode enabled.
      </p>
    </div>
  );
};

const Changelog = () => {
  const [tab, setTab] = useState<"changelog" | "models">("changelog");
  const [search, setSearch] = useState("");
  const [audience, setAudience] = useState<AudienceFilter>("all");
  const [month, setMonth] = useState("");
  const [page, setPage] = useState(0);

  const { data, isLoading, isError, refetch } = useQuery(
    ["changelog-index"],
    fetchChangelogIndex,
    { staleTime: 60 * 1000, retry: 1 }
  );

  const allEntries = useMemo(() => {
    const entries = [...(data?.entries || [])];
    entries.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return entries;
  }, [data]);

  const months = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    allEntries.forEach((e) => {
      const k = monthKey(e.date);
      if (k && !seen.has(k)) {
        seen.add(k);
        out.push(k);
      }
    });
    return out;
  }, [allEntries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allEntries.filter((e) => {
      if (!q) return true;
      const haystack = [
        e.title,
        e.summary,
        ...(e.tags || []),
        ...(e.highlights || []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allEntries, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageEntries = filtered.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );

  const jumpToMonth = (key: string) => {
    setMonth(key);
    if (!key) return;
    const idx = filtered.findIndex((e) => monthKey(e.date) === key);
    if (idx < 0) return;
    const targetPage = Math.floor(idx / PAGE_SIZE);
    setPage(targetPage);
    // Scroll after the page renders.
    setTimeout(() => {
      const el = document.getElementById(`entry-${filtered[idx].id}`);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  return (
    <NavHeader>
      <Container>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 xl:text-3xl">
            What&apos;s New
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Product updates, new models, and improvements to the Passio
            Nutrition AI Platform.
          </p>
        </div>

        <div className="mb-6 flex gap-6 border-b border-gray-200">
          {[
            { id: "changelog" as const, label: "Changelog" },
            { id: "models" as const, label: "Models & System" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={classNames(
                "border-b-2 px-1 pb-3 text-sm font-medium",
                tab === t.id
                  ? "border-passio-indigo text-passio-indigo"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "models" ? (
          <ModelsSection />
        ) : (
          <>
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  placeholder="Search updates…"
                  className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-passio-indigo focus:ring-passio-indigo"
                />
              </div>

              <select
                value={audience}
                onChange={(e) => setAudience(e.target.value as AudienceFilter)}
                className="rounded-md border border-gray-300 py-2 pl-3 pr-8 text-sm focus:border-passio-indigo focus:ring-passio-indigo"
              >
                <option value="all">All changes</option>
                <option value="customer">Customer-facing</option>
                <option value="internal">Internal</option>
              </select>

              <select
                value={month}
                onChange={(e) => jumpToMonth(e.target.value)}
                className="rounded-md border border-gray-300 py-2 pl-3 pr-8 text-sm focus:border-passio-indigo focus:ring-passio-indigo"
              >
                <option value="">Jump to date…</option>
                {months.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </select>
            </div>

            {isLoading && (
              <div className="flex items-center gap-2 py-12 text-sm text-gray-500">
                <Spinner /> Loading changelog…
              </div>
            )}

            {isError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                We couldn&apos;t load the changelog right now.{" "}
                <button
                  type="button"
                  className="font-medium underline"
                  onClick={() => refetch()}
                >
                  Try again
                </button>
                .
              </div>
            )}

            {!isLoading && !isError && filtered.length === 0 && (
              <div className="rounded-md border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
                {allEntries.length === 0
                  ? "No changelog entries have been published yet. Check back soon!"
                  : "No updates match your search."}
              </div>
            )}

            <div className="space-y-4">
              {pageEntries.map((item, i) => (
                <ChangelogEntryCard
                  key={item.id}
                  item={item}
                  audience={audience}
                  defaultOpen={safePage === 0 && i === 0}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-between">
                <button
                  type="button"
                  disabled={safePage === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {safePage + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={safePage >= totalPages - 1}
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}

            {data?.entries && data.entries.length > 0 && (
              <div className="mt-8 text-center">
                <a
                  href={CHANGELOG_INDEX_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                >
                  Raw changelog feed
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                </a>
              </div>
            )}
          </>
        )}
      </Container>
    </NavHeader>
  );
};

export default Changelog;
